import React, { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { difference, differenceBy, isArray, keyBy, orderBy } from "lodash";
import { v4 as uuidv4 } from "uuid";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  CustomTreeData,
  DataTypeProvider,
  IntegratedSelection,
  SelectionState,
  type TableColumnWidthInfo,
  TreeDataState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableFixedColumns,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { type Dimensions } from "@features/sales-v3/charts/chart";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useActiveTab } from "@features/sales-v3/hooks/use-active-tab";
import { useTabState } from "@features/sales-v3/hooks/use-tab-state";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import {
  useDefaultTableSelectionIsSelectAll,
  useSplitView,
  useTableRowSelection,
} from "@features/sales-v3/hooks/use-table-selection";
import { useTableState } from "@features/sales-v3/hooks/use-table-state";
import {
  type SalesTableRow,
  type SalesTableVisualRow,
} from "@features/sales-v3/types/table";
import { useFeatureFlags } from "@hooks/http";
import { PrimaryButton } from "@shared/buttons";

import {
  MaxIndentLevelProvider,
  useExpandedRowIds,
  useTabTopPlatforms,
} from "../contexts";
import { recursivelySearchForRows } from "../utils";

import { TableCell, TableRow } from "./body-components";
import {
  FIXED_NAME_COLUMN_ID,
  ROW_HEIGHT,
  ROW_HEIGHT_COMPACT,
  SELECTION_COLUMN_WIDTH,
  FIXED_STATUS_COLUMN_ID,
} from "./constants";
import {
  DimensionDataFormatter,
  MetricDataFormatter,
  StatusDataFormatter,
} from "./data-type-formatters";
import {
  CustomSalesTableHeadWithFixedRow,
  TableHeaderCell,
  TableHeaderContent,
  TableSelectionHeaderCell,
} from "./header-components";
import { TableSelectionCell } from "./header-components/table-selection-cell";
import {
  TableTreeColumnCell,
  TableTreeColumnExpandButton,
  TableTreeColumnIndent,
} from "./table-tree-components";

interface Props {
  columns: ColumnDef[];
  rows: SalesTableRow[];
  loading: boolean;
  onDimensionCellClick(row: SalesTableVisualRow): void;
  maxIndentLevel: number;
  headingRow: SalesTableVisualRow | undefined;
  loadMore: (options: { breakdownPath: string[]; offset?: number }) => void;
}

const isSubset = (list1: string[], list2: string[]) => {
  // Convert list2 to a Set for faster lookup
  const set2 = new Set(list2);

  // Check if every element in list1 is also in list2
  for (const element of list1) {
    if (!set2.has(element)) {
      return false;
    }
  }

  return true;
};

export const isParent = (
  parent: string,
  child: string,
  ignoreEqual?: boolean,
) => {
  const parsedParent = JSON.parse(parent);
  const parsedChild = JSON.parse(child);

  if (parsedChild.length < parsedParent.length) {
    return false;
  }
  if (!ignoreEqual && parent === child) {
    return false;
  }

  return isSubset(
    parsedParent?.map((i: object) => JSON.stringify(i)),
    isArray(parsedChild)
      ? parsedChild?.map((i: object) => JSON.stringify(i))
      : [parsedChild],
  );
};

export const isKeyParent = (
  adKeyDimensions: { [dimension: string]: string },
  potentialParentAtom: string,
) => {
  try {
    const atom = JSON.parse(potentialParentAtom) as {
      [dimension: string]: string;
    }[];

    return atom.every((dimensions) => {
      return Object.keys(dimensions).every(
        (dimension) => adKeyDimensions[dimension] === dimensions[dimension],
      );
    });
  } catch (error) {
    return false;
  }
};

const LoadMoreRow = ({
  onLoadMore,
  indent,
  isCompacted,
  isInfiniteScroll,
}: {
  onLoadMore(): void;
  indent: number;
  isCompacted: boolean;
  isInfiniteScroll: boolean;
}) => {
  // comment out this useEffect to stop autoloading
  useEffect(() => {
    isInfiniteScroll && onLoadMore();
  }, [onLoadMore, isInfiniteScroll]);

  return (
    <tr
      className={clsx({ relative: true })}
      style={{ height: isCompacted ? ROW_HEIGHT_COMPACT : ROW_HEIGHT }}
    >
      <td className="border-r-0 sticky left-0"></td>
      <td
        className="sticky no-shadow border-r-0"
        style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
      >
        <PrimaryButton
          size="sm"
          onClick={onLoadMore}
          className={clsx({
            sticky: true,
            "mt-1.5": isCompacted,
            "mt-3": !isCompacted,
            "text-xs": isCompacted,
          })}
          style={{
            marginLeft: `${40 * indent + 15}px`,
            height: isCompacted ? "1.75rem" : "2rem",
          }}
        >
          Load more
        </PrimaryButton>
      </td>
      <td className="loading-indicator" colSpan={9999}></td>
    </tr>
  );
};

export const Table = ({
  columns,
  rows,
  onDimensionCellClick,
  loading,
  maxIndentLevel,
  headingRow,
  loadMore,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [expandedRowIds, setExpandedRowIds] = useExpandedRowIds();
  const { activeTab } = useActiveTab();
  const { setTabState } = useTabState(activeTab);
  const { breakdowns } = usePageConfiguration();
  const { selectedRowIds } = useTableRowSelection(rows, activeTab);
  /* Resizing when using query params as the state has a delay in updating
     This method make the resizing more responsive */
  const { columnWidths, updateColumnConfigWidth } = useTableState();

  const [currentColumnWidths, setCurrentColumnWidths] =
    useState<TableColumnWidthInfo[]>(columnWidths);

  const calculatedColumnWidths = useMemo(() => {
    // Initializes with 2 columns (name and status)
    if (currentColumnWidths.length === 2) {
      return columnWidths;
    }

    const currentColumnWidthsMap = new Map(
      currentColumnWidths.map(({ columnName, width }) => [columnName, width]),
    );

    const newColumnWidths = columnWidths.map(({ columnName, width }) => {
      const currentWidth = currentColumnWidthsMap.get(columnName);

      if (currentWidth === undefined) {
        return { columnName, width };
      }

      return { columnName, width: currentWidth };
    });

    return newColumnWidths;
  }, [columnWidths, currentColumnWidths]);

  useEffect(() => {
    setCurrentColumnWidths(columnWidths);
  }, [columnWidths]);

  useEffect(() => {
    const currentColumnWidthsMap = new Map(
      currentColumnWidths.map(({ columnName, width }) => [columnName, width]),
    );

    const hasWidthChanged = columnWidths.some(
      ({ columnName, width }) =>
        currentColumnWidthsMap.get(columnName) !== width,
    );

    if (currentColumnWidths.length === columnWidths.length && hasWidthChanged) {
      updateColumnConfigWidth(currentColumnWidths);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentColumnWidths]);

  const { featureFlags } = useFeatureFlags();
  const isCompacted = useMemo(() => {
    return Boolean(featureFlags?.salesPageV3UsingCompactTableHeight);
  }, [featureFlags]);
  const isInfiniteScroll = useMemo(() => {
    return Boolean(featureFlags?.salesPageV3UsingPaginatedTableExperience);
  }, [featureFlags]);

  // this gets the aggregate values
  const FixedTopRow = useMemo(
    () =>
      CustomSalesTableHeadWithFixedRow(
        headingRow!,
        expandedRowIds,
        () => {
          prettyLogEventPage({
            domain: "Table",
            action: "onClick",
            additionalActionInfo: "Collapse All Rows",
          });

          setExpandedRowIds([]);
        },
        isCompacted,
        rows as SalesTableVisualRow[],
      ),
    [
      rows,
      expandedRowIds,
      headingRow,
      setExpandedRowIds,
      isCompacted,
      prettyLogEventPage,
    ],
  );

  const getChildRows = useCallback(
    (row: SalesTableRow, rootRows: any[] | null) => {
      if (row === null) {
        return rootRows;
      }

      if (row.rowType === "loadMore" || row.rowType === "loading") {
        return null;
      }

      const currentDepth = breakdowns.findIndex(
        (b) => b.key === row.rowMetadata.breakdown,
      );
      const parsedKey = JSON.parse(row.key);
      const noBreakdowns = Array.isArray(parsedKey)
        ? (parsedKey as Dimensions[]).every((k) => !Object.keys(k).length) ||
          (breakdowns.length > 0 &&
            currentDepth !== Object.keys(row.rowMetadata.dimensions).length - 1)
        : false;

      if (noBreakdowns) {
        return null;
      }

      const hasChildren = row.children.length > 0;

      if (hasChildren) {
        return row.children;
      }

      if (row.hasChildren) {
        return [
          {
            rowType: "loading",
            key: `loading-${uuidv4()}`,
            breakdownPath: JSON.parse(row.key),
          },
        ];
      }

      return null;
    },
    [breakdowns],
  );

  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();
  const isSplitView = useSplitView();

  const rowsById = useMemo(() => {
    return keyBy(
      recursivelySearchForRows(rows) as SalesTableVisualRow[],
      "key",
    );
  }, [rows]);

  const [, setTabTopPlatforms] = useTabTopPlatforms(activeTab);
  useEffect(() => {
    const topPlatformsSet = new Set<string>();
    for (const rowId of selectedRowIds) {
      const row = rowsById[rowId];
      const platform = row?.rowMetadata?.dimensions?.["Platform (Northbeam)"];
      if (!platform) {
        continue;
      }
      topPlatformsSet.add(platform);
      if (topPlatformsSet.size === 3) {
        break;
      }
    }
    setTabTopPlatforms(Array.from(topPlatformsSet));
  }, [rowsById, selectedRowIds, setTabTopPlatforms]);

  const onSelectionChange = (_rowIds: (string | number)[]) => {
    prettyLogEventPage({
      domain: "Table",
      action: "onChange",
      additionalActionInfo: "Selection Change",
      data: {
        pageContext: { ids: _rowIds },
      },
    });

    const rowIds = _rowIds.filter((id) => {
      try {
        JSON.parse(id as string);
        return true;
      } catch {
        return false;
      }
    });
    setTabState((prevState) => {
      const getRowCount = (rowId: string) => {
        return rowsById[rowId].rowMetadata.deepestAtomicityCounts;
      };
      /* if it is in the exclusionary mode
         then only care about exclusions */
      if (!isSplitView && isDefaultSelectAll) {
        const removedRowIds = difference(selectedRowIds, rowIds);
        // removedAtomicities
        const removedAtomicities = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];
        const removedAtomicitiesSet = new Set(removedAtomicities);

        // removedAdKeys
        const removedAdKeys = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];
        const removedAdKeysSet = new Set(removedAdKeys);

        const addedRowIds = difference(rowIds, selectedRowIds);
        // added adkeys
        const addedAdKeys = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];
        const addedAdKeysSet = new Set(addedAdKeys);
        // added atomicities
        const addedAtomicities = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];
        const addedAtomicitiesSet = new Set(addedAtomicities);

        const initialExclusionAtoms = removedAtomicities
          .map((d) => ({
            atomicity: d,
            count: getRowCount(d),
          }))
          .concat(prevState.proposedFiltersState.exclusionAtomicities)
          .filter((atomicity) => !addedAtomicitiesSet.has(atomicity.atomicity));
        const initialInclusionAtoms = addedAtomicities
          .map((d) => ({
            atomicity: d,
            count: getRowCount(d),
          }))
          .concat(prevState.proposedFiltersState.inclusionAtomicities)
          .filter(
            (atomicity) => !removedAtomicitiesSet.has(atomicity.atomicity),
          );

        const checkIfAtomParent = (
          potentialParents: string[],
          child: string,
        ) => {
          return potentialParents.some((d) =>
            isParent(
              d,
              child,
              rowsById[d]?.rowMetadata?.naming?.name !==
                rowsById[child]?.rowMetadata?.naming?.name,
            ),
          );
        };

        const cullInclusionChildrenAtoms = initialInclusionAtoms.filter(
          (atomicity) => {
            return (
              !checkIfAtomParent(addedAtomicities, atomicity.atomicity) &&
              !checkIfAtomParent(removedAtomicities, atomicity.atomicity)
            );
          },
        );

        const cullExclusionChildrenAtoms = initialExclusionAtoms.filter(
          (atomicity) => {
            return (
              !checkIfAtomParent(addedAtomicities, atomicity.atomicity) &&
              !checkIfAtomParent(removedAtomicities, atomicity.atomicity)
            );
          },
        );

        const cullExclusionAtomsFromParents = cullExclusionChildrenAtoms.filter(
          (atomicity) => {
            const yourIncParents =
              prevState.proposedFiltersState.inclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);
            const yourExclParents =
              prevState.proposedFiltersState.exclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);
            if (!yourExclParents.length) {
              return true;
            }
            const yourExclDeepestParent = orderBy(
              yourExclParents,
              (p) => p.length,
              "desc",
            );

            const yourIncDeepestParent = orderBy(
              yourIncParents,
              (p) => p.length,
              "desc",
            );

            return (
              (yourIncDeepestParent?.[0]?.length ?? 0) >
              (yourExclDeepestParent?.[0]?.length ?? 0)
            );
          },
        );

        const cullInclusionAtomsFromParents = cullInclusionChildrenAtoms.filter(
          (atomicity) => {
            const yourExclParents =
              prevState.proposedFiltersState.exclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            const yourExclDeepestParent = orderBy(
              yourExclParents,
              (p) => p.length,
              "desc",
            );
            const yourIncParents =
              prevState.proposedFiltersState.inclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            if (!yourIncParents.length) {
              return true;
            }

            const yourIncDeepestParent = orderBy(
              yourIncParents,
              (p) => p.length,
              "desc",
            );

            return (
              (yourIncDeepestParent?.[0]?.length ?? 0) <
              (yourExclDeepestParent?.[0]?.length ?? 0)
            );
          },
        );

        const initialExclusionAdKeys = removedAdKeys
          .map((d) => ({
            adKey: d,
            dimensions: rowsById[d].rowMetadata.dimensions,
          }))
          .concat(prevState.proposedFiltersState.exclusionAdKeys)
          .filter((adKey) => !addedAdKeysSet.has(adKey.adKey));
        const initialInclusionAdKeys = addedAdKeys
          .map((d) => ({
            adKey: d,
            dimensions: rowsById[d].rowMetadata.dimensions,
          }))
          .concat(prevState.proposedFiltersState.inclusionAdKeys)
          .filter((adKey) => !removedAdKeysSet.has(adKey.adKey));

        const checkIfAtomParentForAdKey = (
          potentialParents: string[],
          adKey: string,
        ) => {
          return potentialParents.some((potentialParent) =>
            isKeyParent(
              rowsById[adKey].rowMetadata.dimensions,
              potentialParent,
            ),
          );
        };
        const cullInitialExclusionAdKeys = initialExclusionAdKeys.filter(
          (adKey) => {
            return (
              !checkIfAtomParentForAdKey(addedAtomicities, adKey.adKey) &&
              !checkIfAtomParentForAdKey(removedAtomicities, adKey.adKey)
            );
          },
        );
        const cullInitialInclusionAdKeys = initialInclusionAdKeys.filter(
          (adKey) => {
            return (
              !checkIfAtomParentForAdKey(addedAtomicities, adKey.adKey) &&
              !checkIfAtomParentForAdKey(removedAtomicities, adKey.adKey)
            );
          },
        );

        const result = {
          ...prevState,
          proposedFiltersState: {
            exclusionAtomicities: cullExclusionAtomsFromParents,

            inclusionAtomicities: cullExclusionAtomsFromParents.length
              ? cullInclusionAtomsFromParents
              : [],
            inclusionAdKeys: cullExclusionChildrenAtoms.length
              ? cullInitialInclusionAdKeys
              : [],
            exclusionAdKeys: cullInitialExclusionAdKeys,
          },
        };

        return result;
      } else if (!isSplitView && !isDefaultSelectAll) {
        const removedRowIds = difference(selectedRowIds, rowIds);
        // removedAtomicities
        const removedAtomicities = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];
        const removedAtomicitiesSet = new Set(removedAtomicities);

        // removedAdKeys
        const removedAdKeys = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];
        const removedAdKeysSet = new Set(removedAdKeys);

        const addedRowIds = difference(rowIds, selectedRowIds);
        // added adkeys
        const addedAdKeys = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];
        const addedAdKeysSet = new Set(addedAdKeys);
        // added atomicities
        const addedAtomicities = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];
        const addedAtomicitiesSet = new Set(addedAtomicities);

        const initialExclusionAtoms = removedAtomicities
          .map((d) => ({
            atomicity: d,
            count: getRowCount(d),
          }))
          .concat(prevState.proposedFiltersState.exclusionAtomicities)
          .filter((atomicity) => !addedAtomicitiesSet.has(atomicity.atomicity));
        const initialInclusionAtoms = addedAtomicities
          .map((d) => ({
            atomicity: d,
            count: getRowCount(d),
          }))
          .concat(prevState.proposedFiltersState.inclusionAtomicities)
          .filter(
            (atomicity) => !removedAtomicitiesSet.has(atomicity.atomicity),
          );

        const checkIfAtomParent = (
          potentialParents: string[],
          child: string,
        ) => {
          return potentialParents.some((d) =>
            isParent(
              d,
              child,
              rowsById[d]?.rowMetadata?.naming?.name !==
                rowsById[child]?.rowMetadata?.naming?.name,
            ),
          );
        };

        const cullInclusionChildrenAtoms = initialInclusionAtoms.filter(
          (atomicity) => {
            return (
              !checkIfAtomParent(addedAtomicities, atomicity.atomicity) &&
              !checkIfAtomParent(removedAtomicities, atomicity.atomicity)
            );
          },
        );

        const cullExclusionChildrenAtoms = initialExclusionAtoms.filter(
          (atomicity) => {
            return (
              !checkIfAtomParent(addedAtomicities, atomicity.atomicity) &&
              !checkIfAtomParent(removedAtomicities, atomicity.atomicity)
            );
          },
        );

        const cullExclusionAtomsFromParents = cullExclusionChildrenAtoms.filter(
          (atomicity) => {
            const yourIncParents =
              prevState.proposedFiltersState.inclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            if (!yourIncParents.length) {
              return false;
            }

            const yourExclParents =
              prevState.proposedFiltersState.exclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            const yourExclDeepestParent = orderBy(
              yourExclParents,
              (p) => p.length,
              "desc",
            );

            const yourIncDeepestParent = orderBy(
              yourIncParents,
              (p) => p.length,
              "desc",
            );

            return (
              (yourIncDeepestParent?.[0]?.length ?? 0) >
              (yourExclDeepestParent?.[0]?.length ?? 0)
            );
          },
        );

        const cullInclusionAtomsFromParents = cullInclusionChildrenAtoms.filter(
          (atomicity) => {
            const yourExclParents =
              prevState.proposedFiltersState.exclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            const yourExclDeepestParent = orderBy(
              yourExclParents,
              (p) => p.length,
              "desc",
            );
            const yourIncParents =
              prevState.proposedFiltersState.inclusionAtomicities
                .filter((atom) => isParent(atom.atomicity, atomicity.atomicity))
                .map((i) => JSON.parse(i.atomicity) as Dimensions[]);

            const yourIncDeepestParent = orderBy(
              yourIncParents,
              (p) => p.length,
              "desc",
            );
            if (!yourIncParents.length && !yourExclDeepestParent.length) {
              return true;
            }

            return (
              (yourIncDeepestParent?.[0]?.length ?? 0) <
              (yourExclDeepestParent?.[0]?.length ?? 0)
            );
          },
        );

        const initialExclusionAdKeys = removedAdKeys
          .map((d) => ({
            adKey: d,
            dimensions: rowsById[d].rowMetadata.dimensions,
          }))
          .concat(prevState.proposedFiltersState.exclusionAdKeys)
          .filter((adKey) => !addedAdKeysSet.has(adKey.adKey));
        const initialInclusionAdKeys = addedAdKeys
          .map((d) => ({
            adKey: d,
            dimensions: rowsById[d].rowMetadata.dimensions,
          }))
          .concat(prevState.proposedFiltersState.inclusionAdKeys)
          .filter((adKey) => !removedAdKeysSet.has(adKey.adKey));

        const checkIfAtomParentForAdKey = (
          potentialParents: string[],
          adKey: string,
        ) => {
          return potentialParents.some((potentialParent) =>
            isKeyParent(
              rowsById[adKey].rowMetadata.dimensions,
              potentialParent,
            ),
          );
        };
        const cullInitialExclusionAdKeys = initialExclusionAdKeys.filter(
          (adKey) => {
            return (
              !checkIfAtomParentForAdKey(addedAtomicities, adKey.adKey) &&
              !checkIfAtomParentForAdKey(removedAtomicities, adKey.adKey)
            );
          },
        );
        const cullInitialInclusionAdKeys = initialInclusionAdKeys.filter(
          (adKey) => {
            return (
              !checkIfAtomParentForAdKey(addedAtomicities, adKey.adKey) &&
              !checkIfAtomParentForAdKey(removedAtomicities, adKey.adKey)
            );
          },
        );

        const result = {
          ...prevState,
          proposedFiltersState: {
            ...prevState.proposedFiltersState,
            inclusionAtomicities: cullInclusionAtomsFromParents,
            inclusionAdKeys: cullInitialInclusionAdKeys,
            exclusionAtomicities: cullInclusionAtomsFromParents.length
              ? cullExclusionAtomsFromParents
              : [],
            exclusionAdKeys: cullInclusionChildrenAtoms.length
              ? cullInitialExclusionAdKeys
              : [],
          },
        };

        return result;
      } else {
        // NON SELET ALL MODE

        // removedAdKeys
        const removedRowIds = difference(selectedRowIds, rowIds);

        const removedAdKeys = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];
        const removedAdKeysSet = new Set(removedAdKeys);

        const addedRowIds = difference(rowIds, selectedRowIds);
        // added adkeys
        const addedAdKeys = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return !Array.isArray(parsedKey);
        }) as string[];

        const initialInclusionAdKeys = addedAdKeys
          .map((d) => ({
            adKey: d,
            dimensions: rowsById[d].rowMetadata.dimensions,
          }))
          .concat(prevState.proposedFiltersState.inclusionAdKeys)
          .filter((adKey) => !removedAdKeysSet.has(adKey.adKey));

        const addedAtomicities = addedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];

        // removedAtomicities
        const removedAtomicities = removedRowIds.filter((r) => {
          const parsedKey = JSON.parse(r as string);
          return Array.isArray(parsedKey);
        }) as string[];
        const removedAtomicitiesSet = new Set(removedAtomicities);

        const initialInclusionAtoms = addedAtomicities
          .map((d) => ({
            atomicity: d,
            count: getRowCount(d),
          }))
          .concat(prevState.proposedFiltersState.inclusionAtomicities)
          .filter(
            (atomicity) => !removedAtomicitiesSet.has(atomicity.atomicity),
          );

        return {
          ...prevState,
          proposedFiltersState: {
            ...prevState.proposedFiltersState,
            ...(activeTab !== "breakdown" && {
              inclusionAdKeys: initialInclusionAdKeys,
            }),
            ...(activeTab === "breakdown" && {
              inclusionAtomicities: initialInclusionAtoms,
            }),
          },

          totalCount: 0,
        };
      }
    });
  };

  const possibleLoadingRows = useMemo(() => {
    if (loading) {
      return Array.from({ length: 15 }).map((_, index) => ({
        key: `loading-${index}`,
        rowType: "loading",
        index,
      }));
    } else {
      return rows;
    }
  }, [loading, rows]);

  return (
    <MaxIndentLevelProvider maxIndentLevel={maxIndentLevel}>
      <div
        className={clsx("sales-table-v3 h-[45rem] relative", {
          "sales-table-v3-compact-row-height": isCompacted,
        })}
      >
        <Grid
          columns={columns}
          rows={possibleLoadingRows}
          getRowId={(row) => row.key}
        >
          <SelectionState
            selection={selectedRowIds}
            onSelectionChange={onSelectionChange}
          />
          <IntegratedSelection />
          <TreeDataState
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={(values) => {
              const newExpandedRowIds = differenceBy(values, expandedRowIds);

              prettyLogEventPage({
                domain: "Table",
                action: "onClick",
                additionalActionInfo: "Expand Rows",
                data: {
                  newExpandedRowIds,
                  pageContext: { expandedRowIds },
                },
              });

              for (const id of newExpandedRowIds) {
                const breakdownPath = JSON.parse(id as string);
                loadMore({ breakdownPath });
              }

              setExpandedRowIds(values as string[]);
            }}
          />
          <CustomTreeData getChildRows={getChildRows} />
          <VirtualTable
            height="auto"
            estimatedRowHeight={isCompacted ? ROW_HEIGHT_COMPACT : ROW_HEIGHT}
            headComponent={FixedTopRow as React.ComponentType<object>}
            rowComponent={(props) => {
              const row = props.row;
              if (row.rowType === "loadMore") {
                const indent = row.breakdownPath.length;
                return (
                  <LoadMoreRow
                    isInfiniteScroll={isInfiniteScroll}
                    indent={indent}
                    isCompacted={isCompacted}
                    onLoadMore={() => {
                      prettyLogEventPage({
                        domain: "Table",
                        action: "Other",
                        additionalActionInfo: "Load More Rows",
                      });

                      loadMore({
                        breakdownPath: row.breakdownPath,
                        offset: row.offset,
                      });
                    }}
                  />
                );
              } else if (row.rowType === "loading") {
                return (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={clsx(
                      props.row.index % 2 === 0 ? "even" : "odd",
                      "relative",
                    )}
                    style={{
                      height: isCompacted ? ROW_HEIGHT_COMPACT : ROW_HEIGHT,
                    }}
                  >
                    <td className="sticky left-0 bg-white z-1 border-b border-gray-2"></td>
                    <td
                      className="sticky bg-white z-1 border-b border-gray-2"
                      style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
                    >
                      <div className="flex items-center h-full ml-2">
                        <img
                          src={salesPlatformColumnLoading}
                          alt="Loading table"
                          style={{
                            height: isCompacted ? "1.75rem" : "2rem",
                          }}
                        />
                      </div>
                    </td>
                    {Array.from({ length: columns.length }).map((_, i) => (
                      <td key={i} className="border-b border-gray-2">
                        <div className="animate-pulse flex-grow h-full flex items-center">
                          <div className="ml-2 h-2 w-2/5 bg-gray-2"></div>
                        </div>
                      </td>
                    ))}
                  </motion.tr>
                );
              } else {
                return <TableRow {...props}></TableRow>;
              }
            }}
            cellComponent={TableCell}
          />
          <TableColumnResizing
            columnWidths={calculatedColumnWidths}
            onColumnWidthsChange={(values) => {
              prettyLogEventPage({
                domain: "Table",
                action: "Other",
                additionalActionInfo: "Change Column Widths",
                data: {
                  newColumnWidths: values,
                  pageContext: { columnWidths: calculatedColumnWidths },
                },
              });

              setCurrentColumnWidths(values);
            }}
          />
          <TableHeaderRow
            cellComponent={TableHeaderCell}
            contentComponent={TableHeaderContent}
          />
          <TableSelection
            showSelectAll
            selectionColumnWidth={SELECTION_COLUMN_WIDTH}
            headerCellComponent={TableSelectionHeaderCell}
            cellComponent={TableSelectionCell}
          />
          {/* Custom column cells */}
          <DataTypeProvider
            for={[FIXED_NAME_COLUMN_ID]}
            formatterComponent={(props) => (
              <DimensionDataFormatter
                {...props}
                isCompacted={isCompacted}
                onCellClick={onDimensionCellClick}
              />
            )}
          />
          <DataTypeProvider
            for={[FIXED_STATUS_COLUMN_ID]}
            formatterComponent={StatusDataFormatter}
          />
          <DataTypeProvider
            for={columns
              .map((e) => e.name)
              .filter(
                (name) =>
                  ![FIXED_NAME_COLUMN_ID, FIXED_STATUS_COLUMN_ID].includes(
                    name,
                  ),
              )}
            formatterComponent={MetricDataFormatter}
          />
          <TableTreeColumn
            for={FIXED_NAME_COLUMN_ID}
            cellComponent={(props) => <TableTreeColumnCell {...props} />}
            expandButtonComponent={(props) => (
              <TableTreeColumnExpandButton
                {...props}
                isCompacted={isCompacted}
              />
            )}
            indentComponent={(props) => (
              <TableTreeColumnIndent {...props} isCompacted={isCompacted} />
            )}
          />
          <TableFixedColumns
            leftColumns={[TableSelection.COLUMN_TYPE, FIXED_NAME_COLUMN_ID]}
          />
        </Grid>
      </div>
    </MaxIndentLevelProvider>
  );
};
